import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { generateIndustryServiceSchema } from '@/utils/seoSchema';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import HeroSection from '@/components/social-media/HeroSection';
import WhyKIVideos from '@/components/social-media/WhyKIVideos';
import ProcessSteps from '@/components/social-media/ProcessSteps';
import ExampleGallery from '@/components/social-media/ExampleGallery';
import FAQSection from '@/components/social-media/FAQSection';
import CtaSection from '@/components/social-media/CtaSection';
import FloatingCTA from '@/components/social-media/FloatingCTA';

const FinanceIndustry = () => {
  const { lang, city } = useParams();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  
  useEffect(() => {
    // SEO meta tags setup based on language
    const titlesByLang = {
      de: city ? `KI-Videomarketing für Finanzdienstleister ${city} | Future Level` : "KI-Videomarketing für Finanzdienstleister | Future Level",
      en: city ? `AI Video Marketing for Financial Services ${city} | Future Level` : "AI Video Marketing for Financial Services | Future Level",
      fr: city ? `Marketing Vidéo IA pour Services Financiers ${city} | Future Level` : "Marketing Vidéo IA pour Services Financiers | Future Level",
      it: city ? `Marketing Video AI per Servizi Finanziari ${city} | Future Level` : "Marketing Video AI per Servizi Finanziari | Future Level"
    };
    
    const descriptionsByLang = {
      de: city ? 
        `Professionelle KI-generierte Videos für Banken, Versicherungen und Finanzberater in ${city}. Steigern Sie Vertrauen und Conversion mit maßgeschneiderten Finanz-Videos.` : 
        "Professionelle KI-generierte Videos für Banken, Versicherungen und Finanzberater. Steigern Sie Vertrauen und Conversion mit maßgeschneiderten Finanz-Videos.",
      en: city ?
        `Professional AI-generated videos for banks, insurance companies, and financial advisors in ${city}. Increase trust and conversion with tailored financial videos.` :
        "Professional AI-generated videos for banks, insurance companies, and financial advisors. Increase trust and conversion with tailored financial videos.",
      fr: city ?
        `Vidéos professionnelles générées par IA pour banques, assurances et conseillers financiers à ${city}. Augmentez la confiance et la conversion avec des vidéos financières sur mesure.` :
        "Vidéos professionnelles générées par IA pour banques, assurances et conseillers financiers. Augmentez la confiance et la conversion avec des vidéos financières sur mesure.",
      it: city ?
        `Video professionali generati da IA per banche, assicurazioni e consulenti finanziari a ${city}. Aumenta la fiducia e la conversione con video finanziari personalizzati.` :
        "Video professionali generati da IA per banche, assicurazioni e consulenti finanziari. Aumenta la fiducia e la conversione con video finanziari personalizzati."
    };
    
    const keywordsByLang = {
      de: city ? 
        `KI-Videomarketing Finanzsektor ${city}, Banken Marketing Videos KI ${city}, Versicherung Erklärvideo ${city}, Finanzdienstleister KI-Videos, Fintech Videomarketing` :
        "KI-Videomarketing Finanzsektor, Banken Marketing Videos KI, Versicherung Erklärvideo, Finanzdienstleister KI-Videos, Fintech Videomarketing",
      en: city ?
        `AI video marketing financial sector ${city}, bank marketing videos AI ${city}, insurance explainer video ${city}, financial services AI videos, fintech video marketing` :
        "AI video marketing financial sector, bank marketing videos AI, insurance explainer video, financial services AI videos, fintech video marketing",
      fr: city ?
        `Marketing vidéo IA secteur financier ${city}, vidéos marketing banques IA ${city}, vidéo explicative assurance ${city}, services financiers vidéos IA, marketing vidéo fintech` :
        "Marketing vidéo IA secteur financier, vidéos marketing banques IA, vidéo explicative assurance, services financiers vidéos IA, marketing vidéo fintech",
      it: city ?
        `Marketing video AI settore finanziario ${city}, video marketing banche AI ${city}, video esplicativo assicurazione ${city}, servizi finanziari video AI, marketing video fintech` :
        "Marketing video AI settore finanziario, video marketing banche AI, video esplicativo assicurazione, servizi finanziari video AI, marketing video fintech"
    };
    
    // Determine the canonical URL path based on language
    const pagePath = currentLanguage === 'de' ? '/leistungen/ki-videomarketing-finanzsektor' : 
                    currentLanguage === 'en' ? '/services/ai-video-marketing-finance' : 
                    currentLanguage === 'fr' ? '/services/video-marketing-ia-finance' : 
                    '/servizi/marketing-video-ia-finanza';
                    
    const canonicalUrl = getCanonicalUrl(`${pagePath}${city ? `/${city.toLowerCase()}` : ''}`, currentLanguage);
    
    // Update meta tags for SEO
    updateMetaTags({
      title: titlesByLang[currentLanguage as keyof typeof titlesByLang],
      description: descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      keywords: keywordsByLang[currentLanguage as keyof typeof keywordsByLang],
      canonicalUrl: canonicalUrl,
      language: currentLanguage,
      ogType: "website"
    });
    
    // Add structured data with industry-specific schema
    generateIndustryServiceSchema(
      'finance',
      titlesByLang[currentLanguage as keyof typeof titlesByLang],
      descriptionsByLang[currentLanguage as keyof typeof descriptionsByLang],
      "https://future-level.ch/opengraph-image-p98pqg.png",
      canonicalUrl,
      currentLanguage,
      city
    );
    
  }, [currentLanguage, city]);
  
  // Industry-specific content (would be customized for finance industry)
  const getHeroContent = () => {
    const content = {
      de: {
        title: city ? `KI-Videomarketing für Finanzdienstleister in ${city}` : 'KI-Videomarketing für Finanzdienstleister',
        subtitle: 'Steigern Sie Vertrauen und Transparenz mit maßgeschneiderten KI-Videos für Banken, Versicherungen und Finanzberater',
        description: 'Nutzen Sie die Kraft des KI-Videomarketings, um komplexe Finanzprodukte verständlich zu erklären, Vertrauen aufzubauen und Kundenbeziehungen zu stärken - DSGVO-konform und mit absolutem Fokus auf Datenschutz.',
      },
      en: {
        title: city ? `AI Video Marketing for Financial Services in ${city}` : 'AI Video Marketing for Financial Services',
        subtitle: 'Increase trust and transparency with tailored AI videos for banks, insurance companies, and financial advisors',
        description: 'Harness the power of AI video marketing to explain complex financial products clearly, build trust, and strengthen customer relationships - GDPR compliant and with absolute focus on data protection.',
      },
      fr: {
        title: city ? `Marketing Vidéo IA pour Services Financiers à ${city}` : 'Marketing Vidéo IA pour Services Financiers',
        subtitle: 'Augmentez la confiance et la transparence avec des vidéos IA sur mesure pour banques, assurances et conseillers financiers',
        description: 'Exploitez la puissance du marketing vidéo IA pour expliquer clairement des produits financiers complexes, établir la confiance et renforcer les relations clients - conforme au RGPD et avec un focus absolu sur la protection des données.',
      },
      it: {
        title: city ? `Marketing Video AI per Servizi Finanziari a ${city}` : 'Marketing Video AI per Servizi Finanziari',
        subtitle: 'Aumenta fiducia e trasparenza con video AI personalizzati per banche, assicurazioni e consulenti finanziari',
        description: 'Sfrutta il potere del marketing video AI per spiegare chiaramente prodotti finanziari complessi, costruire fiducia e rafforzare le relazioni con i clienti - conforme al GDPR e con focus assoluto sulla protezione dei dati.',
      }
    };
    
    return content[currentLanguage as keyof typeof content] || content.en;
  };
  
  const heroContent = getHeroContent();
  
  return (
    <div className="flex flex-col min-h-screen bg-[#121212]">
      <Header />
      <PageTransition>
        <main className="flex-grow pt-24">
          <HeroSection 
            title={heroContent.title} 
            subtitle={heroContent.subtitle} 
            description={heroContent.description}
            industry="finance"
          />
          
          <WhyKIVideos industry="finance" />
          
          <ProcessSteps industry="finance" />
          
          <section className="py-16 px-4 md:px-8 bg-gradient-to-br from-purple-900/20 to-black">
            <div className="container mx-auto max-w-6xl">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white text-center">
                {currentLanguage === 'de' ? 'Finanzspezifische Lösungen' :
                 currentLanguage === 'en' ? 'Financial-Specific Solutions' :
                 currentLanguage === 'fr' ? 'Solutions Spécifiques au Secteur Financier' :
                 'Soluzioni Specifiche per il Settore Finanziario'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Erklärvideo für Bankprodukte' :
                     currentLanguage === 'en' ? 'Explainer Videos for Banking Products' :
                     currentLanguage === 'fr' ? 'Vidéos Explicatives pour Produits Bancaires' :
                     'Video Esplicativi per Prodotti Bancari'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Komplexe Bankprodukte einfach und verständlich erklärt - ideal für Ihre Kunden und Interessenten.' :
                     currentLanguage === 'en' ? 'Complex banking products explained simply and understandably - ideal for your customers and prospects.' :
                     currentLanguage === 'fr' ? 'Produits bancaires complexes expliqués simplement et de manière compréhensible - idéal pour vos clients et prospects.' :
                     'Prodotti bancari complessi spiegati in modo semplice e comprensibile - ideale per i tuoi clienti e potenziali clienti.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Versicherungsprodukt-Videos' :
                     currentLanguage === 'en' ? 'Insurance Product Videos' :
                     currentLanguage === 'fr' ? 'Vidéos de Produits d\'Assurance' :
                     'Video di Prodotti Assicurativi'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Machen Sie Versicherungsprodukte greifbar und transparent durch ansprechende visuelle Erklärungen.' :
                     currentLanguage === 'en' ? 'Make insurance products tangible and transparent through appealing visual explanations.' :
                     currentLanguage === 'fr' ? 'Rendez les produits d\'assurance tangibles et transparents grâce à des explications visuelles attrayantes.' :
                     'Rendi i prodotti assicurativi tangibili e trasparenti attraverso spiegazioni visive accattivanti.'}
                  </p>
                </div>
                
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {currentLanguage === 'de' ? 'Investment & Portfolio Videos' :
                     currentLanguage === 'en' ? 'Investment & Portfolio Videos' :
                     currentLanguage === 'fr' ? 'Vidéos d\'Investissement & Portefeuille' :
                     'Video di Investimento e Portafoglio'}
                  </h3>
                  <p className="text-gray-300">
                    {currentLanguage === 'de' ? 'Präsentieren Sie Anlagestrategien und Portfolios mit ansprechenden visuellen Inhalten für Ihre Kunden.' :
                     currentLanguage === 'en' ? 'Present investment strategies and portfolios with appealing visual content for your customers.' :
                     currentLanguage === 'fr' ? 'Présentez les stratégies d\'investissement et les portefeuilles avec un contenu visuel attrayant pour vos clients.' :
                     'Presenta strategie di investimento e portafogli con contenuti visivi accattivanti per i tuoi clienti.'}
                  </p>
                </div>
              </div>
            </div>
          </section>
          
          <ExampleGallery industry="finance" />
          
          <FAQSection industry="finance" />
          
          <CtaSection industry="finance" />
        </main>
      </PageTransition>
      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default FinanceIndustry;
